import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import RevealWrapper from '@/pages/home/components/RevealWrapper';
import gSoftLogoDark from '@/pages/images/g-soft-logo-beyaz2.png';
import { useProductProgress } from '@/context/ProductProgressContext';
/* Kozmik Günlük ilerleme barı */
const MOCK_ENTRIES = [
  { titleKey: 'kg.mock.entry1.title', moodKey: 'kg.mock.entry1.mood', time: '08:30' },
  { titleKey: 'kg.mock.entry2.title', moodKey: 'kg.mock.entry2.mood', time: '14:15' },
  { titleKey: 'kg.mock.entry3.title', moodKey: 'kg.mock.entry3.mood', time: '22:00' },
  { titleKey: 'kg.mock.entry4.title', moodKey: 'kg.mock.entry4.mood', time: '06:45' },
];

function ProgressBar() {
  const { t } = useTranslation();
  const progressValue = useProductProgress('kozmik-gunluk');
  const [displayed, setDisplayed] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let startTimer: ReturnType<typeof setTimeout> | null = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startTimer = setTimeout(() => setStarted(true), 700);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => {
      observer.disconnect();
      if (startTimer) clearTimeout(startTimer);
    };
  }, []);

  useEffect(() => {
    if (!started) return;
    let frame: number;
    const start = performance.now();
    const duration = 1400;
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(eased * progressValue));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [started, progressValue]);

  return (
    <div ref={ref} className="mt-10 mb-2">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-xs text-purple-400/50 tracking-widest uppercase">{t('kg.progress.label')}</span>
        <span className="font-mono text-2xl font-black text-purple-300 tabular-nums">{displayed}<span className="text-purple-400/40 text-base">%</span></span>
      </div>
      <div className="relative h-1.5 bg-purple-500/10 w-full overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full bg-gradient-to-r from-purple-500 to-purple-400 transition-none"
          style={{
            width: started ? `${progressValue}%` : '0%',
            transition: started ? 'width 1.4s cubic-bezier(0.16,1,0.3,1)' : 'none',
          }}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="font-mono text-[10px] text-purple-400/30 tracking-widest">{t('kg.progress.version')}</span>
        <span className="font-mono text-[10px] text-purple-400/30 tracking-widest">{t('kg.progress.status')}</span>
      </div>
    </div>
  );
}

export default function HeroBlock() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen bg-[#0c0c0c] flex flex-col overflow-hidden pt-16">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-purple-900/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-indigo-900/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-violet-900/5 rounded-full blur-[150px]" />
      </div>
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div key={i} className="absolute w-0.5 h-0.5 bg-white/30 rounded-full animate-pulse"
            style={{ top: `${(i * 37) % 100}%`, left: `${(i * 53) % 100}%`, animationDelay: `${(i * 0.3) % 3}s` }} />
        ))}
      </div>
      <div className="absolute top-24 left-0 w-px h-64 bg-gradient-to-b from-transparent via-purple-500/20 to-transparent" />
      <div className="absolute top-24 left-0 w-64 h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />

      <div className="flex-1 flex flex-col justify-center px-6 lg:px-14 py-16 relative z-10">
        <RevealWrapper direction="up" duration={500}>
          <div className="flex items-center gap-3 mb-10">
            <button onClick={() => navigate('/')} className="font-mono text-xs text-neutral-600 hover:text-neutral-400 tracking-widest uppercase transition-colors duration-200 flex items-center gap-2 cursor-pointer">
              <i className="ri-arrow-left-line" />{t('product.back')}
            </button>
            <span className="text-neutral-800">/</span>
            <span className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{t('product.breadcrumb.products')}</span>
            <span className="text-neutral-800">/</span>
            <span className="font-mono text-xs text-purple-400 tracking-widest uppercase">{t('kg.breadcrumb')}</span>
          </div>
        </RevealWrapper>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <RevealWrapper direction="left" duration={700}>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono text-xs px-2.5 py-1 border border-purple-500/30 text-purple-400 bg-purple-500/5 tracking-widest">{t('kg.status')}</span>
                <span className="font-mono text-xs text-neutral-600 tracking-widest">{t('kg.meta')}</span>
              </div>
              <div className="flex items-center gap-5 mb-6">
                <div className="w-20 h-20 border border-purple-500/20 flex items-center justify-center bg-[#161616] shrink-0 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent" />
                  <img src={gSoftLogoDark} alt={t('kg.name')} className="w-16 h-16 object-contain relative z-10" />
                </div>
                <h1 className="font-black text-white text-5xl lg:text-6xl tracking-tight leading-none">{t('kg.name.line1')}<br />{t('kg.name.line2')}</h1>
              </div>
              <p className="text-neutral-400 text-base leading-relaxed max-w-md mb-10">
                {t('kg.description')}
              </p>
              <div className="flex flex-wrap gap-4">
                <button disabled className="inline-flex items-center gap-2 px-7 py-3.5 bg-purple-600/20 text-purple-400 font-mono text-xs font-bold tracking-widest uppercase cursor-not-allowed sm:whitespace-nowrap border border-purple-500/30">
                  <i className="ri-time-line text-base" />{t('kg.cta.soon')}
                </button>
                <a href="#features" className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white font-mono text-xs tracking-widest uppercase hover:border-purple-500/50 hover:text-purple-400 transition-all duration-300 sm:whitespace-nowrap">
                  <i className="ri-sparkling-line" />{t('kg.cta.features')}
                </a>
              </div>
              <ProgressBar />
            </div>
          </RevealWrapper>

          <RevealWrapper direction="right" delay={200} duration={700}>
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-500/10 blur-3xl rounded-full scale-75" />
                <div className="relative bg-[#1a1a1a] border border-purple-500/20 rounded-[36px] overflow-hidden shadow-2xl" style={{ width: 260, height: 520 }}>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#1a1a1a] rounded-b-2xl z-10 flex items-center justify-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500/50" />
                    <div className="w-8 h-1 rounded-full bg-neutral-800" />
                  </div>
                  <div className="w-full h-full flex flex-col bg-gradient-to-b from-[#0d0d12] via-[#0a0a0f] to-[#0d0d12]">
                    <div className="flex items-center justify-between px-6 pt-8 pb-2">
                      <span className="font-mono text-[10px] text-purple-400/60">9:41</span>
                      <div className="w-4 h-2 border border-purple-500/30 rounded-sm"><div className="w-3/4 h-full bg-purple-500/50 rounded-sm" /></div>
                    </div>
                    <div className="flex items-center justify-between px-5 py-4 border-b border-purple-500/10">
                      <div className="flex items-center gap-2">
                        <img src={gSoftLogoDark} alt={t('kg.mock.appShortName')} className="w-7 h-7 object-contain rounded-lg" />
                        <span className="font-mono text-[11px] text-purple-400 font-bold">{t('kg.mock.appShortName')}</span>
                      </div>
                      <i className="ri-add-line text-purple-400/60 text-lg" />
                    </div>
                    <div className="mx-4 mt-4 p-4 border border-purple-500/20 bg-gradient-to-br from-purple-500/5 to-transparent rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-moon-line text-purple-400 text-sm" />
                        <span className="font-mono text-[10px] text-purple-400/80">{t('kg.mock.today')}</span>
                      </div>
                      <div className="font-black text-white text-xl mb-1">{t('kg.mock.day')}</div>
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-[10px] text-purple-400" />
                        <span className="font-mono text-[10px] text-purple-400/60">{t('kg.mock.starsCollected')}</span>
                      </div>
                    </div>
                    <div className="flex-1 px-4 py-3 overflow-hidden">
                      <div className="font-mono text-[9px] text-purple-400/40 tracking-widest uppercase mb-3">{t('kg.mock.recentEntries')}</div>
                      {MOCK_ENTRIES.map((entry, i) => (
                        <div key={i} className="flex items-center justify-between py-3 border-b border-purple-500/10 last:border-0">
                          <div>
                            <div className="font-mono text-[10px] text-white mb-0.5">{t(entry.titleKey)}</div>
                            <div className="font-mono text-[9px] text-purple-400/60">{t(entry.moodKey)}</div>
                          </div>
                          <span className="font-mono text-[9px] text-purple-400/40">{entry.time}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-around px-4 py-3 border-t border-purple-500/10">
                      {['ri-moon-line', 'ri-star-line', 'ri-planet-line', 'ri-settings-3-line'].map((icon, i) => (
                        <div key={i} className={`w-10 h-10 flex items-center justify-center ${i === 0 ? 'text-purple-400' : 'text-purple-400/30'}`}>
                          <i className={`${icon} text-lg`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </RevealWrapper>
        </div>
      </div>

      <div className="border-t border-purple-500/10 px-6 lg:px-14 py-4 flex items-center justify-between relative z-10">
        <span className="font-mono text-xs text-purple-400/40">{t('kg.footer.left')}</span>
        <span className="font-mono text-xs text-purple-400/40">{t('kg.footer.right')}</span>
      </div>
    </section>
  );
}

